import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Modal, GeneralButton } from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { usePostData } from "../../../hooks";
import { useToast } from "../../../Services/ToastContext";
import { useAppDispatch } from "../../../hooks";
import { resetUserDetails } from "../../../redux/reducers/registrationSlice";
import { resetUserState } from "../../../redux/reducers/userSlice";
import { persistor } from "../../../redux/store";
import ic_deactivate from "../../../assets/images/icons/ic_deactivate.svg";
import ConfirmPasswordForm from "./ConfirmPasswordForm";

interface DeactivateAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DeactivateAccountModal: React.FC<DeactivateAccountModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");  

  const { mutate: checkPasswordMutate, isPending: isPendingCheckPassword } =
    usePostData(EndPoints.CheckPassword);
  const { mutate, isPending } = usePostData(EndPoints.DeActivateAccount);
 
  const handlePasswordCheck = (data: { Password: string }) => {
    setPassword(data.Password);  

    checkPasswordMutate(
      { Password: data.Password },  
      {
        onSuccess: (response) => {
          if (response.Status) {
            setStep(2);  
          } else {
            showToast(response.ErrorMessage || t("invalid_password"), "error");
          }
        },
        onError: (error: any) => {
          showToast(
            error.response?.data?.ErrorMessage || error.message,
            "error",
          );
        },
      },
    );
  };

  const deleteAccountFun = () => {
    mutate(
      { Password: password }, 
      {
        onSuccess: (data) => {
          if (data.Status) {
            showToast(t("deleted_successfully"), "success");
            dispatch(resetUserDetails());
            dispatch(resetUserState());
            localStorage.removeItem("token");
            window.location.reload();
            persistor.purge();
            navigate("/login", { replace: true });
          } else {
            showToast(data.ErrorMessage || "Something went wrong", "error");
          }
        },
        onError: (error: any) => {
          showToast(
            error.response?.data?.ErrorMessage || error.message,
            "error",
          );
        },
      },
    );
  };

  const handleClose = () => {
    setStep(1);
    setPassword(""); 
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      customStyle="!min-w-80 !w-fit "
      header={step === 1 ? t("delete_account") : undefined}
      onClose={handleClose}
    >
      <div>
        {step === 1 && (
          <>
            <ConfirmPasswordForm
              onSubmitSuccess={handlePasswordCheck} 
              onCancel={handleClose}
              isPending={isPendingCheckPassword}
            />
          </>
        )}
        {step === 2 && (
          <>
            <div className="text-center">
              <img
                className="mx-auto my-12 h-8 w-20"
                alt="dome-care-bg"
                src={ic_deactivate}
              />
              <h3 className="mb-2 text-xl font-bold text-error">
                {t("warning")}
              </h3>
              <p className="mb-5 px-4 text-lg text-body-text">
                {t("delete_account_warning_message")}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3">
              <GeneralButton
                color="lite"
                fontColor="body-text"
                title={t("cancel")}
                onClick={handleClose}
              />
              <GeneralButton
                color="red"
                title={t("delete_account")}
                onClick={deleteAccountFun}
                isLoading={isPending}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DeactivateAccountModal;
